import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ModelModalities from "../pages/customization/ModelModalities";
import ModelTags from "../pages/customization/ModelTags";
import { Modality, ModalityDirection, Model } from "../store/global-types/customization-types";
import { VersionTags } from "./VersionTags";

interface ModalitiesAndTagsProps {
    model: Model;
    onModelModalityToggled?: (modality: Modality, direction: ModalityDirection) => void;
    onModelTagToggled?: (tag: string) => void;
    onModelVersionTagToggled?: (tag: string) => void;
    activeTags?: string[];
    activeVersionTags?: string[];
    activeInputModalities?: Modality[];
    activeOutputModalities?: Modality[];
}

const ModalitiesAndTags = ({
    model,
    onModelModalityToggled,
    onModelTagToggled,
    onModelVersionTagToggled,
    activeTags = [],
    activeVersionTags = [],
    activeInputModalities = [],
    activeOutputModalities = []
}: ModalitiesAndTagsProps) => {
    const { t } = useTranslation();

    // Memoize computed values to prevent unnecessary re-renders
    const modelVersionTags = useMemo(() => {
        const tags: string[] = [];

        if (model.isLatest) {
            tags.push(t('customizationPage.modelModalities.latestVersion'));
        }

        if (model.version) {
            tags.push(model.version);
        }

        return tags;
    }, [model.version, model.isLatest, t]);

    const hasInputModalities = model.inputModalities.length > 0;
    const hasOutputModalities = model.outputModalities.length > 0;
    const hasModalities = hasInputModalities || hasOutputModalities;
    const hasTags = model.tags.length > 0;
    const hasVersionTags = modelVersionTags.length > 0;

    const handleModalityToggled = useCallback((
        modality: Modality,
        direction?: ModalityDirection
    ) => {
        if (onModelModalityToggled && direction) {
            onModelModalityToggled(modality, direction);
        }
    }, [onModelModalityToggled]);

    // Early return if nothing to render
    if (!hasModalities && !hasTags && !hasVersionTags) {
        return null;
    }

    return (
        <div className="modalities-and-tags-container">
            {hasInputModalities && (
                <div className="modalities-and-tags-block">
                    <ModelModalities
                        modalities={model.inputModalities}
                        direction={ModalityDirection.Input}
                        label={t('customizationPage.modelModalities.inputModalities', {
                            count: model.inputModalities.length
                        })}
                        onToggle={handleModalityToggled}
                        selectedModalities={activeInputModalities}
                    />
                </div>
            )}

            {hasOutputModalities && (
                <div className="modalities-and-tags-block">
                    <ModelModalities
                        modalities={model.outputModalities}
                        direction={ModalityDirection.Output}
                        label={t('customizationPage.modelModalities.outputModalities', {
                            count: model.outputModalities.length
                        })}
                        onToggle={handleModalityToggled}
                        selectedModalities={activeOutputModalities}
                    />
                </div>
            )}

            {hasVersionTags && (
                <div className="modalities-and-tags-block">
                    <VersionTags
                        tags={modelVersionTags}
                        onToggle={onModelVersionTagToggled}
                        showLabel={true}
                        useContainer={true}
                        allowInteraction={true}
                        activeTags={activeVersionTags}
                    />
                </div>
            )}

            {hasTags && (
                <div className="modalities-and-tags-block">
                    <ModelTags
                        tags={model.tags}
                        onToggle={onModelTagToggled}
                        activeTags={activeTags}
                    />
                </div>
            )}
        </div>
    );
};

export default ModalitiesAndTags;